import {HttpClient} from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import {BACKEND_URL, BASE_URL} from '@aztrix/environment';
import {iconNameForCommunicationType} from '@aztrix/helpers';
import {CommunicationType} from '@aztrix/models';
import {
  AgreementDataRepresentation,
  AgreementRepresentation,
  ProfileRepresentation,
  ProposalRepresentation,
} from '@aztrix/sdk';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  ReplaySubject,
  switchMap,
} from 'rxjs';

@Component({
  selector: 'ax-agreement-extra-info',
  templateUrl: './agreement-extra-info.component.html',
  styleUrls: ['../property.scss', './agreement-extra-info.component.scss'],
})
export class AgreementExtraInfoComponent implements OnChanges {
  @Input() myProfile?: ProfileRepresentation;
  @Input() proposal?: ProposalRepresentation;
  @Input() agreement?: AgreementRepresentation;
  @Input() agreementData?: AgreementDataRepresentation;

  @Input() historyData: AgreementDataRepresentation[];
  @Output() didClickSendEmail = new EventEmitter();

  communicationType = CommunicationType.MAIL;
  iconNameForCommunicationType = iconNameForCommunicationType;

  private _proposal$ = new ReplaySubject<ProposalRepresentation | undefined>(undefined);
  private _agreement$ = new ReplaySubject<AgreementRepresentation | undefined>(undefined);
  private _agreementData$ = new ReplaySubject<AgreementDataRepresentation | undefined>(undefined);
  private _myProfile$ = new ReplaySubject<ProfileRepresentation | undefined>(undefined);

  owner$: Observable<ProfileRepresentation | undefined> = this._proposal$.pipe(
    mergeMap((proposal) => (proposal ? this._getProfile(proposal.ownerId) : of(undefined)))
  );

  isOwner$: Observable<boolean> = combineLatest([this._myProfile$, this.owner$]).pipe(
    map(([myProfile, owner]) => {
      if (myProfile?.id === owner?.id) {
        return true;
      } else {
        return false;
      }
    })
  );

  languageCode$ = this._agreementData$.pipe(
    filter((d) => !!d),
    map((d) => d?.language)
  );

  constructor(
    private _http: HttpClient,
    @Optional() @Inject(BASE_URL) public baseUrl: string,
    @Inject(BACKEND_URL) public backendUrl$: BehaviorSubject<string>
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (this.agreement && changes.agreement) {
      this._agreement$.next(this.agreement);
    }

    if (this.proposal && changes.proposal) {
      this._proposal$.next(this.proposal);
    }

    if (this.agreementData && changes.agreementData) {
      this._agreementData$.next(this.agreementData);
    }

    if (this.myProfile && changes.myProfile) {
      this._myProfile$.next(this.myProfile);
    }
  }

  private _getProfile(
    profileId: string | undefined
  ): Observable<ProfileRepresentation | undefined> {
    if (!profileId) {
      return of(undefined);
    }
    return this.backendUrl$.pipe(
      switchMap((backendUrl) =>
        this._http.get<ProfileRepresentation>(`${backendUrl || ''}/r/profiles/${profileId}`)
      )
    );
  }
}
